import { useEffect, useMemo, useState } from "react";

import { log } from "utils/log";

import { fetchPreviewBitmap, type PreviewSizing } from "./fetchPreviewBitmap";
import { type TiledDrawingSource } from "./tileMath";

const l = log.miniMap.tiled;

/**
 * Mini map bitmap for tiled drawings (consumed inside `useDrawingImage`):
 * fetches the preview tier fitting the consumer's `tierMaxSize` (web 768 →
 * `small` 512 px, mobile 1536 → `medium` 1536 px) and decodes it no larger
 * than `decodeMaxSize`. Ownership mirrors the PDF hook: the bitmap is closed
 * on source/sizing change or unmount and never handed out detached.
 *
 * Returns `undefined` while loading, without a source, or without `sizing`
 * (the large maps paint tiles per viewport instead — `useTiledLargeMap`).
 */
export function useTiledDrawingImage(
    source: TiledDrawingSource | undefined,
    sizing: PreviewSizing | undefined,
): ImageBitmap | undefined {
    const [entry, setEntry] = useState<{ source: TiledDrawingSource; bitmap: ImageBitmap }>();
    const tierMaxSize = sizing?.tierMaxSize;
    const decodeMaxSize = sizing?.decodeMaxSize;
    // Depend on the two sizes rather than the object, so a caller that builds
    // `sizing` inline (every one of them does) doesn't re-fetch and re-decode
    // the preview on every render.
    const stableSizing = useMemo<PreviewSizing | undefined>(
        () => (tierMaxSize === undefined ? undefined : { tierMaxSize, decodeMaxSize }),
        [tierMaxSize, decodeMaxSize],
    );

    useEffect(() => {
        if (!source || !stableSizing) {
            return;
        }

        let cancelled = false;
        let bitmap: ImageBitmap | undefined;
        fetchPreviewBitmap(source, stableSizing)
            .then((bmp) => {
                if (cancelled) {
                    bmp.close();
                    return;
                }
                bitmap = bmp;
                setEntry({ source, bitmap: bmp });
            })
            .catch((error) => {
                l.error("Failed to load tiled drawing preview:", error);
            });

        return () => {
            cancelled = true;
            if (bitmap) {
                bitmap.close();
            }
            setEntry((cur) => (cur?.bitmap === bitmap ? undefined : cur));
        };
    }, [source, stableSizing]);

    // Only expose a bitmap belonging to the *current* source that hasn't been
    // released (a closed bitmap reports width 0 and would throw in drawImage).
    if (entry && entry.source === source && entry.bitmap.width > 0) {
        return entry.bitmap;
    }
    return undefined;
}
